import { TurnId } from "@taskin-code/contracts";

export const CHAT_RIGHT_PANELS = ["browser", "diff", "files", "sidechat"] as const;

export type ChatRightPanel = (typeof CHAT_RIGHT_PANELS)[number];

// Single source of truth for "is this a panel we can render?". Route search params, persisted
// store state, and the panel switches all validate through here, so adding a panel means
// touching one list instead of hunting for open-coded comparisons.
export function isChatRightPanel(value: unknown): value is ChatRightPanel {
  return typeof value === "string" && (CHAT_RIGHT_PANELS as ReadonlyArray<string>).includes(value);
}

export interface DiffRouteSearch {
  splitViewId?: string | undefined;
  panel?: ChatRightPanel | undefined;
  diff?: "1" | undefined;
  diffTurnId?: TurnId | undefined;
  diffFilePath?: string | undefined;
}

function isDiffOpenValue(value: unknown): boolean {
  return value === "1" || value === 1 || value === true;
}

function normalizeSearchString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}

export function stripDiffSearchParams<T extends Record<string, unknown>>(
  params: T,
): Omit<T, "panel" | "diff" | "diffTurnId" | "diffFilePath"> {
  const {
    panel: _panel,
    diff: _diff,
    diffTurnId: _diffTurnId,
    diffFilePath: _diffFilePath,
    ...rest
  } = params;
  return rest as Omit<T, "panel" | "diff" | "diffTurnId" | "diffFilePath">;
}

export function parseDiffRouteSearch(search: Record<string, unknown>): DiffRouteSearch {
  const splitViewId = normalizeSearchString(search.splitViewId);
  const panelRaw = normalizeSearchString(search.panel);
  const panel: ChatRightPanel | undefined = isChatRightPanel(panelRaw) ? panelRaw : undefined;
  const diff = panel === "diff" || isDiffOpenValue(search.diff) ? "1" : undefined;
  const resolvedPanel = panel ?? (diff ? "diff" : undefined);
  const diffTurnIdRaw = diff ? normalizeSearchString(search.diffTurnId) : undefined;
  const diffTurnId = diffTurnIdRaw ? TurnId.makeUnsafe(diffTurnIdRaw) : undefined;
  const diffFilePath = diff && diffTurnId ? normalizeSearchString(search.diffFilePath) : undefined;

  return {
    ...(splitViewId ? { splitViewId } : {}),
    ...(resolvedPanel ? { panel: resolvedPanel } : {}),
    ...(diff ? { diff } : {}),
    ...(diffTurnId ? { diffTurnId } : {}),
    ...(diffFilePath ? { diffFilePath } : {}),
  };
}
